import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { availableAfesAtom } from '../atoms';
import {
  EditAfeSourceModalView,
  type EditAfeSourceModalViewProps,
} from 'pages/packages/show/edit-afe-source-modal/edit-afe-source-modal-view';

type EditAfeSourceModalProps = Omit<
  EditAfeSourceModalViewProps,
  'data' | 'isLoading' | 'error'
>;

/**
 * Edit AFE Source modal — Jotai data wrapper. Owns the lazy `availableAfesAtom`
 * fetch (triggered once per open) and feeds the presentational
 * `EditAfeSourceModalView`. Public props are unchanged so existing callers are
 * unaffected; the eval3-new feature renders the View directly with
 * React-Query-supplied data instead.
 *
 * @param {EditAfeSourceModalProps} props - The modal state, callbacks, and slot context.
 * @return {JSX.Element} The Edit AFE Source modal.
 */
export const EditAfeSourceModal = (props: EditAfeSourceModalProps) => {
  const { isOpen, slotId } = props;
  const hasTriggeredRef = useRef(false);

  // Fetch available AFEs using lazy atom (triggers on demand)
  const [afesLoadable, triggerFetch] = useAtom(
    availableAfesAtom({ slotId: slotId || '' })
  );

  // Extract primitive values to use as stable deps
  const state = afesLoadable.state;
  const isDataNull = state === 'hasData' ? afesLoadable.data === null : false;

  // Reset trigger flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasTriggeredRef.current = false;
    }
  }, [isOpen]);

  // Trigger fetch ONCE per modal open (idle or error)
  useEffect(() => {
    if (isOpen && slotId && !hasTriggeredRef.current) {
      const isIdle = state === 'hasData' && isDataNull;
      const isError = state === 'hasError';

      if (isIdle || isError) {
        hasTriggeredRef.current = true;
        triggerFetch();
      }
    }
  }, [isOpen, slotId, state, isDataNull, triggerFetch]);

  const isLoading = afesLoadable.state === 'loading';
  const error =
    afesLoadable.state === 'hasError'
      ? afesLoadable.error instanceof Error
        ? afesLoadable.error.message
        : 'Failed to load available AFEs'
      : null;
  const data = afesLoadable.state === 'hasData' ? afesLoadable.data : null;

  return (
    <EditAfeSourceModalView
      {...props}
      data={data}
      isLoading={isLoading}
      error={error}
    />
  );
};
